import {
  BACKOFFICE_GET_SEARCH_RATE_LIMIT_CONFIG_ERROR,
  BACKOFFICE_GET_SEARCH_RATE_LIMIT_CONFIG_RESPONSE,
  BACKOFFICE_GET_SEARCH_RATE_LIMIT_VIOLATIONS_ERROR,
  BACKOFFICE_GET_SEARCH_RATE_LIMIT_VIOLATIONS_RESPONSE,
  BACKOFFICE_UPDATE_SEARCH_RATE_LIMIT_CONFIG_ERROR,
  BACKOFFICE_UPDATE_SEARCH_RATE_LIMIT_CONFIG_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { QueryResponseType } from '@1po/1po-bff-fe-spec/generated/common/WsResponse';
import { all, call, put, select, takeEvery } from '@redux-saga/core/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import { RootState } from 'app/AppStore';
import { WsResponse } from 'domains/webSockets/WebSocket.types';
import { notifyTop } from 'UI';
import { AppTranslation, sagaGuard } from 'utils';
import {
  sendGetSearchRateLimitConfig,
  sendGetSearchRateLimitViolations,
  sendUpdateSearchRateLimitConfig,
} from './SearchRateLimit.api';
import {
  fetchSearchRateLimitConfigRequest,
  fetchSearchRateLimitConfigNotFound,
  fetchSearchRateLimitViolationsFailure,
  fetchSearchRateLimitViolationsRequest,
  setSearchRateLimitConfig,
  setSearchRateLimitViolations,
  searchRateLimitUpdateConfigTimedOut,
  updateSearchRateLimitConfigFailure,
  updateSearchRateLimitConfigRequest,
  updateSearchRateLimitConfigSuccess,
} from './SearchRateLimit.store';
import {
  GarageLimitOverride,
  SearchRateLimitConfig,
  SearchRateLimitViolation,
  SearchRateLimitViolationMetrics,
} from './SearchRateLimit.types';

type SearchRateLimitViolationsPayload = {
  violations?: SearchRateLimitViolation[];
  totalCount?: number;
  totalViolationCount?: number;
  shortTermMetrics?: SearchRateLimitViolationMetrics;
  longTermMetrics?: SearchRateLimitViolationMetrics;
};

type UpdateSearchRateLimitConfigResponsePayload = {
  result?: string;
  body?: unknown;
};

type SaveOutcome = 'success' | 'no-change' | 'error';

const DEFAULT_ERROR_MESSAGE = 'Update failed';

// The BFF pushes a CommandResponse. Depending on whether the WS layer remaps an
// intermediate command-processing frame, the outcome is carried either in
// `queryResponseType` (payload unwrapped to the command body) or in `payload.result`
// (direct push while `queryResponseType` is still INIT). Handle both shapes.
const OUTCOME_BY_QUERY_TYPE: Partial<Record<QueryResponseType, SaveOutcome>> = {
  [QueryResponseType.COMMAND_RESPONSE_SUCCESS]: 'success',
  [QueryResponseType.COMMAND_RESPONSE_NO_CHANGE]: 'no-change',
  [QueryResponseType.COMMAND_RESPONSE_ERROR]: 'error',
  [QueryResponseType.COMMAND_RESPONSE_INVALID]: 'error',
};

const OUTCOME_BY_RESULT: Record<string, SaveOutcome> = {
  SUCCESS: 'success',
  NO_CHANGE: 'no-change',
  ERROR: 'error',
  INVALID_COMMAND: 'error',
  AGGREGATE_NOT_FOUND: 'error',
};

const getResultFromPayload = (payload: unknown): string | undefined => {
  if (typeof payload === 'object' && payload !== null && 'result' in payload) {
    const result = (payload as { result?: unknown }).result;
    return typeof result === 'string' ? result.toUpperCase() : undefined;
  }
  return undefined;
};

const resolveSaveOutcome = (
  action: WsResponse<UpdateSearchRateLimitConfigResponsePayload | boolean | string>,
): SaveOutcome => {
  const byQueryType = action.queryResponseType ? OUTCOME_BY_QUERY_TYPE[action.queryResponseType] : undefined;
  if (byQueryType) {
    return byQueryType;
  }

  const result = getResultFromPayload(action.payload);
  if (result && OUTCOME_BY_RESULT[result]) {
    return OUTCOME_BY_RESULT[result];
  }

  // This saga only handles UPDATE_CONFIG_RESPONSE frames, which are only ever
  // pushed as a save outcome. A missing marker means a plain success ack.
  return 'success';
};

function* notifySaveSuccess(): SagaIterator {
  yield call(
    notifyTop,
    'success',
    AppTranslation.t('backoffice.search-rate-limit.save.success', 'Search rate limit config updated successfully'),
  );
}

function* notifySaveNoChange(): SagaIterator {
  yield call(notifyTop, 'info', AppTranslation.t('backoffice.search-rate-limit.save.no-change', 'No changes to save'));
}

function* notifySaveError(): SagaIterator {
  yield call(
    notifyTop,
    'error',
    AppTranslation.t('backoffice.search-rate-limit.save.error', 'Failed to update search rate limit config'),
  );
}

function* applySaveOutcome(
  outcome: SaveOutcome,
  config: SearchRateLimitConfig | null | undefined,
  errorMessage = DEFAULT_ERROR_MESSAGE,
): SagaIterator {
  if (outcome === 'success' || outcome === 'no-change') {
    yield call(outcome === 'success' ? notifySaveSuccess : notifySaveNoChange);
    if (config) {
      yield put(updateSearchRateLimitConfigSuccess(config));
    }
    return;
  }

  yield call(notifySaveError);
  yield put(updateSearchRateLimitConfigFailure(errorMessage));
  if (config?.countryCode) {
    yield put(fetchSearchRateLimitConfigRequest(config.countryCode));
  }
}

// ─── Request sagas ─────────────────────────────────────────────────────────

export function* fetchSearchRateLimitConfigRequestSaga(action: PayloadAction<string>): SagaIterator {
  yield put(sendGetSearchRateLimitConfig({ countryCode: action.payload }));
}

export function* updateSearchRateLimitConfigRequestSaga(action: PayloadAction<SearchRateLimitConfig>): SagaIterator {
  const { garageOverrides, ...rest } = action.payload;
  const requestPayload = {
    ...rest,
    garageOverrides: Object.values(garageOverrides ?? {}),
  };
  yield put(sendUpdateSearchRateLimitConfig(requestPayload));
}

// ─── Response sagas ────────────────────────────────────────────────────────

export function* fetchSearchRateLimitConfigResponseSaga(
  action: WsResponse<{ config: SearchRateLimitConfig & { garageOverrides: GarageLimitOverride[] } }>,
): SagaIterator {
  const rawConfig = action.payload?.config;
  if (!rawConfig) {
    return;
  }

  const overridesRecord: Record<string, GarageLimitOverride> = {};
  for (const override of rawConfig.garageOverrides ?? []) {
    overridesRecord[override.garageId] = override;
  }

  yield put(setSearchRateLimitConfig({ ...rawConfig, garageOverrides: overridesRecord }));
}

export function* updateSearchRateLimitConfigResponseSaga(
  action: WsResponse<UpdateSearchRateLimitConfigResponsePayload | boolean | string>,
): SagaIterator {
  const config: SearchRateLimitConfig | null | undefined = yield select(
    (state: RootState) => state.searchRateLimit.config,
  );
  const outcome = resolveSaveOutcome(action);
  const errorMessage = getResultFromPayload(action.payload) ?? DEFAULT_ERROR_MESSAGE;

  yield call(applySaveOutcome, outcome, config, errorMessage);
}

export function* updateSearchRateLimitConfigTimedOutSaga(): SagaIterator {
  const config: SearchRateLimitConfig | null | undefined = yield select(
    (state: RootState) => state.searchRateLimit.config,
  );

  yield call(applySaveOutcome, 'error', config, 'Request timed out');
}

export function* updateSearchRateLimitConfigErrorSaga(): SagaIterator {
  const config: SearchRateLimitConfig | null | undefined = yield select(
    (state: RootState) => state.searchRateLimit.config,
  );

  yield call(applySaveOutcome, 'error', config, DEFAULT_ERROR_MESSAGE);
}

// ─── Violations sagas ──────────────────────────────────────────────────────

export function* fetchSearchRateLimitViolationsRequestSaga(action: PayloadAction<string>): SagaIterator {
  yield put(sendGetSearchRateLimitViolations({ countryCode: action.payload }));
}

export function* fetchSearchRateLimitViolationsResponseSaga(
  action: WsResponse<SearchRateLimitViolationsPayload>,
): SagaIterator {
  const violations = action.payload?.violations ?? [];
  const totalCount = action.payload?.totalCount ?? action.payload?.totalViolationCount ?? 0;
  yield put(
    setSearchRateLimitViolations({
      violations,
      totalCount,
      shortTermMetrics: action.payload?.shortTermMetrics ?? null,
      longTermMetrics: action.payload?.longTermMetrics ?? null,
    }),
  );
}

export function* fetchSearchRateLimitViolationsErrorSaga(): SagaIterator {
  yield put(fetchSearchRateLimitViolationsFailure());
}

export function* fetchSearchRateLimitConfigErrorSaga(): SagaIterator {
  yield put(fetchSearchRateLimitConfigNotFound());
}

// ─── Root saga ─────────────────────────────────────────────────────────────

export function* searchRateLimitSaga(): SagaIterator {
  yield all([
    takeEvery(fetchSearchRateLimitConfigRequest.type, sagaGuard(fetchSearchRateLimitConfigRequestSaga)),
    takeEvery(BACKOFFICE_GET_SEARCH_RATE_LIMIT_CONFIG_RESPONSE, sagaGuard(fetchSearchRateLimitConfigResponseSaga)),
    takeEvery(BACKOFFICE_GET_SEARCH_RATE_LIMIT_CONFIG_ERROR, sagaGuard(fetchSearchRateLimitConfigErrorSaga)),
    takeEvery(updateSearchRateLimitConfigRequest.type, sagaGuard(updateSearchRateLimitConfigRequestSaga)),
    takeEvery(BACKOFFICE_UPDATE_SEARCH_RATE_LIMIT_CONFIG_RESPONSE, sagaGuard(updateSearchRateLimitConfigResponseSaga)),
    takeEvery(BACKOFFICE_UPDATE_SEARCH_RATE_LIMIT_CONFIG_ERROR, sagaGuard(updateSearchRateLimitConfigErrorSaga)),
    takeEvery(searchRateLimitUpdateConfigTimedOut.type, sagaGuard(updateSearchRateLimitConfigTimedOutSaga)),
    takeEvery(fetchSearchRateLimitViolationsRequest.type, sagaGuard(fetchSearchRateLimitViolationsRequestSaga)),
    takeEvery(
      BACKOFFICE_GET_SEARCH_RATE_LIMIT_VIOLATIONS_RESPONSE,
      sagaGuard(fetchSearchRateLimitViolationsResponseSaga),
    ),
    takeEvery(BACKOFFICE_GET_SEARCH_RATE_LIMIT_VIOLATIONS_ERROR, sagaGuard(fetchSearchRateLimitViolationsErrorSaga)),
  ]);
}
